import { Button, OverlayTrigger, Popover } from "react-bootstrap";
import { FaLock } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useExplorerConnectionLimit } from "../../hooks/useExplorerConnectionLimit";
import { getExplorerWeeklyResetLabel } from "../../utils/explorerConnectionLimitCopy";

type Props = {
  action: "connect" | "accept";
  className?: string;
  connectClassName?: string;
  style?: React.CSSProperties;
  /** Opens the Explorer connection limit paywall modal. */
  onLimitClick?: () => void;
};

/**
 * Locked Connect/Accept — same card layout as the active button, with a short popover on hover/focus.
 */
export function ExplorerConnectionLimitLockedButton({
  action,
  className = "",
  connectClassName = "",
  style,
  onLimitClick,
}: Props) {
  const { t, i18n } = useTranslation();
  const { used, limit, weekResetsUtc } = useExplorerConnectionLimit();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const label =
    action === "accept" ? t("connections.accept") : t("connections.connect");

  const shortMessage = t("connections.explorerWeeklyLimitShort", {
    used,
    limit,
  });
  const resetLabel = getExplorerWeeklyResetLabel(t, locale, weekResetsUtc);

  const popover = (
    <Popover id={`explorer-limit-${action}`}>
      <Popover.Body className="small">
        <div className="fw-semibold mb-1">{shortMessage}</div>
        <div className="text-secondary">{resetLabel}</div>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger trigger={["hover", "focus"]} placement="top" overlay={popover}>
      <span className={`d-inline-flex flex-fill min-w-0 ${className}`.trim()}>
        <Button
          type="button"
          variant="primary"
          className={`rounded-2 fw-medium border-0 shadow-sm text-white action-button opacity-75 w-100 ${connectClassName}`.trim()}
          style={style}
          tabIndex={0}
          aria-disabled="true"
          onClick={(e) => {
            e.stopPropagation();
            onLimitClick?.();
          }}
        >
          <span className="d-inline-flex align-items-center justify-content-center gap-2">
            <FaLock size={12} aria-hidden />
            {label}
          </span>
        </Button>
      </span>
    </OverlayTrigger>
  );
}
